import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  LogOut,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { companyAPI } from '../../services/api';
import SIDEBAR_MENU from '../../config/sidebarMenu';
import { getVisibleSidebar } from '../../lib/sidebarFilter';
import { isItemActive } from '../../lib/sidebarMatchers';
import useStaffSidebarBadges from '../../hooks/useStaffSidebarBadges';
import useAdminSidebarBadges from '../../hooks/useAdminSidebarBadges';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';

// Re-export so existing test imports `from '../components/layout/Sidebar'`
// still resolve. The helper itself lives in lib/sidebarMatchers.js so it
// has zero React/router dependencies and stays unit-testable in isolation.
export { isItemActive };

const Sidebar = ({ user, onLogout, collapsed, setCollapsed }) => {
  const { badges: staffBadges, clearBadge: clearStaffBadge } = useStaffSidebarBadges(user);
  const { badges: adminBadges, clearBadge: clearAdminBadge } = useAdminSidebarBadges(user);
  // Only one of the two ever returns badges (staff vs company/planner side).
  const sidebarBadges = { ...staffBadges, ...adminBadges };
  const clearBadge = (id) => { clearStaffBadge(id); clearAdminBadge(id); };
  const childBadgeFor = (child) => (child?.id ? sidebarBadges[child.id] : null);
  const rollupFor = (section) => (section.children || []).reduce(
    (sum, c) => sum + (c.id && sidebarBadges[c.id] ? Number(sidebarBadges[c.id].value || 0) : 0),
    0
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileCollapsed, setMobileCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    inbox: false,
    events: false,
    ops: false,
    network: false,
    settings: false,
    platform: false,
  });
  const [hoveredSection, setHoveredSection] = useState(null);
  const hoverTimeoutRef = React.useRef(null);

  // isNarrow drives label/content hiding on both mobile (mobileCollapsed) and desktop (collapsed)
  const isNarrow = collapsed || mobileCollapsed;

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleSectionHover = (sectionId) => {
    if (!isNarrow) return;
    clearHoverTimeout();
    setHoveredSection(sectionId);
  };

  const handleSectionLeave = () => {
    if (!isNarrow) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 250);
  };

  const handleFlyoutEnter = () => {
    clearHoverTimeout();
  };

  const handleFlyoutLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearHoverTimeout();
  }, []);

  // Auto-expand the section whose child matches the current route (accordion:
  // opening one closes the others). Restores the remembered open/closed
  // behaviour the menu had before — the /control-center home keeps all folds
  // closed since none of its children match.
  useEffect(() => {
    const path = location.pathname;
    setExpandedSections(prev => {
      const next = { ...prev };
      let touched = false;
      const visible = getVisibleSidebar(SIDEBAR_MENU, user);
      visible.forEach((s) => {
        if (!s.children?.length) return;
        const childMatches = s.children.some(c => isItemActive(c, path));
        if (childMatches && !prev[s.id]) {
          Object.keys(next).forEach(k => { next[k] = false; });
          next[s.id] = true;
          touched = true;
        }
      });
      return touched ? next : prev;
    });
  }, [location.pathname, user]);

  const [companyLogo, setCompanyLogo] = useState(user?.companyLogo || null);

  // Fetch company logo on mount and listen for updates
  useEffect(() => {
    if (user?.companyLogo) {
      setCompanyLogo(user.companyLogo);
    } else {
      // Try fetching from company settings
      const fetchLogo = async () => {
        try {
          const data = await companyAPI.getSettings();
          if (data.logo) setCompanyLogo(data.logo);
        } catch (e) {
          // Silently fail
        }
      };
      fetchLogo();
    }
  }, [user?.companyLogo]);

  // Listen for company settings updates
  useEffect(() => {
    const handleUpdate = (event) => {
      const { companyLogo: newLogo } = event.detail;
      if (newLogo !== undefined) setCompanyLogo(newLogo);
    };
    window.addEventListener('companySettingsUpdated', handleUpdate);
    return () => window.removeEventListener('companySettingsUpdated', handleUpdate);
  }, []);

  const isStaff = user?.role === 'staff';

  // Toggle a section fold. Accordion behaviour: opening a section closes the
  // others; clicking an open section closes it. (Matches the prior behaviour.)
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const isCurrentlyOpen = !!prev[sectionId];
      const next = { ...prev };
      if (isCurrentlyOpen) {
        next[sectionId] = false;
      } else {
        Object.keys(next).forEach(k => { next[k] = k === sectionId; });
      }
      return next;
    });
  };

  // Single matcher that honors `exact: true` on items — see top-of-file helper.
  // Kept inline alias here so the rest of this component reads cleanly.

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  // Whether ANY child of a section currently matches the active route. Uses
  // per-child `isItemActive` so children flagged `exact: true` (e.g. CORE >
  // Events on path "/events") don't prefix-match sibling-section routes
  // and incorrectly light up the parent dropdown.
  const isParentActive = (children) => children.some(c => isItemActive(c, location.pathname));

  /** Among sibling children, only the most specific matching path is "active" (prevents double selection). */
  const getActiveChildPath = (children) => {
    if (!children?.length) return null;
    const matches = children
      .filter((c) => isItemActive(c, location.pathname))
      .sort((a, b) => b.path.length - a.path.length);
    return matches[0]?.path ?? null;
  };

  // Config-driven sidebar: one function call replaces 3 hardcoded arrays + conditional logic
  const sections = getVisibleSidebar(SIDEBAR_MENU, user);
  // Clear-on-view: opening a badged page marks its notifications read no matter how
  // you got there (sidebar click, collapsed group header, deep link, back button).
  // This is what makes a stuck number actually disappear once you've looked at it.
  useEffect(() => {
    if (!Array.isArray(sections)) return;
    const flat = [];
    sections.forEach((s) => {
      flat.push(s);
      (s.children || []).forEach((ch) => flat.push(ch));
    });
    // Most-specific (longest) matching path wins so a parent prefix never steals it.
    const match = flat
      .filter((it) => it && it.path && it.path.length > 1 && location.pathname.startsWith(it.path))
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (match && sidebarBadges[match.id]) {
      clearBadge(match.id);
    }
  }, [location.pathname, sections, sidebarBadges, clearBadge]);

  const handleNavClick = (path) => {
    // Deep-link items that open a modal at the destination (e.g. the
    // "Embed Code" sidebar entry which routes to
    // `/events/automation?embed=open`) need to remember WHERE the user
    // came from, so closing the modal can return them to the prior
    // page instead of stranding them on the modal's host route. We
    // detect any path with `?embed=open` and stamp `state.from` with
    // the current location.
    if (typeof path === 'string' && path.includes('embed=open')) {
      const origin = `${location.pathname}${location.search || ''}`;
      navigate(path, { state: { from: origin } });
    } else {
      navigate(path);
    }
    setMobileCollapsed(false);
    document.body.classList.remove('mobile-sidebar-open');
  };

  const closeMobileSidebar = () => {
    setMobileCollapsed(false);
    document.body.classList.remove('mobile-sidebar-open');
  };

  return (
    <TooltipProvider delayDuration={200}>
      {/* Mobile overlay - always in DOM, CSS controls visibility */}
      <div
        className="mobile-sidebar-overlay fixed inset-0 bg-black/60 z-[59] lg:hidden pointer-events-none opacity-0 transition-opacity duration-300"
        onClick={closeMobileSidebar}
      />
      <aside
        className={cn(
          "mobile-sidebar fixed left-0 top-0 flex flex-col z-[60] transition-all duration-300",
          collapsed ? "lg:w-[72px]" : "lg:w-[260px]",
          mobileCollapsed ? "w-[72px]" : "w-[280px]",
          "-translate-x-full lg:translate-x-0"
        )}
        style={{ background: 'linear-gradient(180deg, #0A0E1A 0%, #070B14 100%)', borderRight: '1px solid rgba(255,255,255,0.04)', height: '100dvh' }}
      >
      {/* Logo — always STAFFED */}
      <div className="h-[68px] flex items-center px-5 border-b border-white/[0.05]">
        <Link to={isStaff ? "/staff-portal" : "/dashboard"} className="flex items-center gap-3">
          <img src={`${process.env.PUBLIC_URL || ''}/staffed-logo.png`} alt="STAFFED" className="w-10 h-10 rounded-xl object-contain flex-shrink-0" />
          {!isNarrow && (
            <span className="text-[20px] tracking-[3px] text-white/90" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>STAFFED</span>
          )}
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-5 px-3 min-h-0">
        <div className="space-y-1.5">
          {sections.map((section) => (
            <div key={section.id}>
              {section.isTopLevel ? (
                // Compute once per render so the highlight class, inline
                // style, and icon-tint all agree — and so an item flagged
                // `exact: true` (e.g. ADMIN PANEL on /admin) doesn't
                // prefix-match sibling routes like /admin/unattributed-leads.
                (() => {
                  const active = isItemActive(section, location.pathname);
                  return (
                    <button
                      onClick={() => { if (sidebarBadges[section.id]) clearBadge(section.id); handleNavClick(section.path); }}
                      className={cn(
                        "relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-medium transition-all duration-300",
                        active
                          ? "text-[#D4AF37]"
                          : "text-white/50 hover:text-white/80 hover:bg-white/[0.03]"
                      )}
                      style={active ? { background: 'rgba(212,175,55,0.06)', borderLeft: '2px solid #D4AF37' } : {}}
                    >
                      <section.icon className={cn("w-5 h-5 flex-shrink-0", active ? "text-[#D4AF37]" : "text-white/30")} />
                      {!isNarrow && <span>{section.label}</span>}
                      {sidebarBadges[section.id] && (
                        isNarrow ? (
                          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#D4AF37]" />
                        ) : sidebarBadges[section.id].kind === 'points' ? (
                          <span className="ml-auto inline-flex items-center px-2 h-5 rounded-full text-[11px] font-bold text-[#0B0F1A] bg-gradient-to-r from-[#F59E0B] to-[#D4AF37] animate-pulse">
                            +{sidebarBadges[section.id].value}
                          </span>
                        ) : (
                          <span className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-bold bg-[#D4AF37] text-[#0B0F1A]">
                            {sidebarBadges[section.id].value > 99 ? '99+' : sidebarBadges[section.id].value}
                          </span>
                        )
                      )}
                    </button>
                  );
                })()
              ) : (
                <div
                  className="sidebar-dropdown-block relative"
                  onMouseEnter={() => handleSectionHover(section.id)}
                  onMouseLeave={handleSectionLeave}
                >
                  <button
                    type="button"
                    onClick={() => { if (!isNarrow) toggleSection(section.id); }}
                    aria-expanded={!!expandedSections[section.id]}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 rounded-xl text-[11px] font-medium tracking-[1.5px] transition-all duration-300",
                      isParentActive(section.children)
                        ? "text-[#D4AF37]"
                        : "text-white/35 hover:text-white/60"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <section.icon className="w-4 h-4 flex-shrink-0 opacity-40" />
                      {!isNarrow && <span className="uppercase">{section.label}</span>}
                    </div>
                    {/* Roll-up dot on the narrow rail (absolute, so it never affects flow) */}
                    {(() => {
                      const roll = rollupFor(section);
                      if (!roll || !isNarrow) return null;
                      return <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#D4AF37]" />;
                    })()}
                    {/* Wide: roll-up dot + fold chevron */}
                    {!isNarrow && (
                      <div className="ml-auto flex items-center gap-2">
                        {rollupFor(section) ? <span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> : null}
                        <ChevronDown className={cn("sidebar-chevron w-4 h-4 flex-shrink-0 text-white/25", expandedSections[section.id] && "rotate-180 text-white/40")} />
                      </div>
                    )}
                  </button>

                  {/* Fold panel — height animated in normal document flow so
                      every row keeps its own vertical space and no two rows
                      ever overlap (at rest or mid-animation). */}
                  {!isNarrow && (
                    <div className={cn("sidebar-fold", expandedSections[section.id] && "sidebar-fold--open")}>
                      <div className="sidebar-fold-inner">
                      <div
                        className="ml-4 pl-4 space-y-1 mt-1 pb-1"
                        style={{
                          borderLeft: '1px solid rgba(212,175,55,0.15)',
                        }}
                      >
                        {section.children.map((child, idx) => {
                          const activeChildPath = getActiveChildPath(section.children);
                          const isChildActive = child.path === activeChildPath;
                          const btn = (
                            <button
                              onClick={() => { if (child.id && sidebarBadges[child.id]) clearBadge(child.id); handleNavClick(child.path); }}
                              className={cn(
                                "sidebar-dropdown-item sidebar-dropdown-item--visible w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px]",
                                isChildActive
                                  ? "text-[#D4AF37]"
                                  : "text-white/45 hover:text-white/70 hover:bg-white/[0.03]"
                              )}
                              style={{
                                ...(isChildActive ? { background: 'rgba(212,175,55,0.06)' } : {}),
                                transitionDelay: '0ms',
                              }}
                            >
                              <child.icon className={cn("w-4 h-4 flex-shrink-0", isChildActive ? "text-[#D4AF37]" : "opacity-35")} />
                              <span>{child.label}</span>
                              {childBadgeFor(child) && (
                                <span className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-bold bg-[#D4AF37] text-[#0B0F1A]">
                                  {childBadgeFor(child).value > 99 ? '99+' : childBadgeFor(child).value}
                                </span>
                              )}
                            </button>
                          );
                          if (!child.tooltip) {
                            return <React.Fragment key={child.path}>{btn}</React.Fragment>;
                          }
                          return (
                            <Tooltip key={child.path}>
                              <TooltipTrigger asChild>{btn}</TooltipTrigger>
                              <TooltipContent side="right">{child.tooltip}</TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                      </div>
                    </div>
                  )}

                  {/* Flyout popup when sidebar is collapsed */}
                  {isNarrow && hoveredSection === section.id && (
                    <div
                      className="fixed z-[200]"
                      style={{
                        left: collapsed ? '72px' : '72px',
                        top: 'var(--flyout-top)',
                      }}
                      onMouseEnter={handleFlyoutEnter}
                      onMouseLeave={handleFlyoutLeave}
                      ref={(el) => {
                        if (el) {
                          const parent = el.previousElementSibling?.previousElementSibling || el.closest('.sidebar-dropdown-block')?.querySelector('button');
                          if (parent) {
                            const rect = parent.getBoundingClientRect();
                            el.style.top = `${rect.top}px`;
                          }
                        }
                      }}
                      data-testid={`flyout-${section.id}`}
                    >
                      <div 
                        className="ml-2 py-2 px-1 rounded-xl border border-white/[0.08] shadow-2xl shadow-black/80 min-w-[200px] animate-in fade-in slide-in-from-left-2 duration-200"
                        style={{ background: 'linear-gradient(135deg, #0F1420 0%, #0A0E1A 100%)', backdropFilter: 'blur(20px)' }}
                      >
                        <div className="px-3 py-2 mb-1 border-b border-white/[0.06]">
                          <span className="text-[10px] font-semibold tracking-[1.5px] uppercase text-white/30">{section.label}</span>
                        </div>
                        {section.children.map((child) => {
                          const activeChildPath = getActiveChildPath(section.children);
                          const isChildActive = child.path === activeChildPath;
                          return (
                            <button
                              key={child.path}
                              onClick={() => { if (child.id && sidebarBadges[child.id]) clearBadge(child.id); handleNavClick(child.path); setHoveredSection(null); }}
                              className={cn(
                                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-all duration-200",
                                isChildActive
                                  ? "text-[#D4AF37] bg-[#D4AF37]/[0.06]"
                                  : "text-white/50 hover:text-white/80 hover:bg-white/[0.04]"
                              )}
                              data-testid={`flyout-item-${child.path.replace(/\//g, '-')}`}
                            >
                              <child.icon className={cn("w-4 h-4 flex-shrink-0", isChildActive ? "text-[#D4AF37]" : "opacity-40")} />
                              <span>{child.label}</span>
                              {childBadgeFor(child) && (
                                <span className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[11px] font-bold bg-[#D4AF37] text-[#0B0F1A]">
                                  {childBadgeFor(child).value > 99 ? '99+' : childBadgeFor(child).value}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>

      {/* Sidebar Edge Toggle — stationary, right edge, vertically centered */}
      {/* Handles both mobile and desktop collapse/expand */}
      <button
        onClick={() => {
          if (window.innerWidth >= 1024) {
            setCollapsed(!collapsed);
          } else {
            setMobileCollapsed(prev => !prev);
          }
        }}
        className="absolute top-1/2 -right-3.5 w-7 h-12 rounded-r-xl flex items-center justify-center bg-[#0A0E1A] border border-l-0 border-white/10 text-white/40 hover:text-[#D4AF37] z-[61] shadow-lg"
        style={{ transform: 'translateY(-50%)' }}
        data-testid="sidebar-edge-toggle"
      >
        <ChevronLeft className={cn("w-4 h-4 transition-transform duration-300", isNarrow && "rotate-180")} />
      </button>

      <div className="flex-shrink-0 p-3 space-y-1 bg-[#070B14]" style={{ borderTop: '1px solid rgba(255,255,255,0.04)', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}>
        <button
          onClick={onLogout}
          data-testid="sign-out-btn"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-medium text-white/40 hover:text-white/70 hover:bg-white/[0.03] transition-all duration-300"
        >
          <LogOut className="w-4.5 h-4.5 flex-shrink-0" />
          {!isNarrow && <span>Sign Out</span>}
        </button>
      </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
